const escapeSrcdocFragment = (value) => (
  (value || '')
    .replace(/<\/script/gi, '<\\/script')
    .replace(/<!--/g, '<\\!--')
);

/**
 * Build a self-contained HTML document for template iframe previews.
 * @param {{ name?: string, html_code?: string, css_code?: string, js_code?: string }} template
 * @param {{ thumbnail?: boolean }} options - thumbnail mode caps hero height for gallery cards
 */
export function buildTemplatePreviewHtml(template, { thumbnail = false } = {}) {
  let html = escapeSrcdocFragment(template?.html_code || '');
  const css = escapeSrcdocFragment(template?.css_code || '');
  const js = escapeSrcdocFragment(template?.js_code || '');

  const linkTagMatches = html.match(/<link[^>]+>/gi) || [];
  const extractedLinks = linkTagMatches.join('\n');
  html = html.replace(/<link[^>]+>/gi, '');

  let primaryColor = '#7c3aed';
  let secondaryColor = '#9333ea';
  const rootMatch = css.match(/:root\s*\{([^}]+)\}/);
  if (rootMatch) {
    const rootBlock = rootMatch[1];
    const primaryMatch = rootBlock.match(/--primary(?:-color)?:\s*(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|[a-z]+)/);
    const secondaryMatch = rootBlock.match(/--secondary(?:-color)?:\s*(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|[a-z]+)/);
    if (primaryMatch) primaryColor = primaryMatch[1];
    if (secondaryMatch) secondaryColor = secondaryMatch[1];
  }

  let fontFamily = 'Inter';
  const fontMatch = css.match(/font-family:\s*['"]?([^,'";\n]+)/);
  if (fontMatch) fontFamily = fontMatch[1].trim().replace(/['"]/g, '');

  const thumbnailCss = thumbnail ? `
    html, body { overflow: hidden !important; }
    body.template-thumbnail-preview .widget-section:not(.header-section):not(.hero-section) {
      display: none !important;
    }
    .hero-section, .hero, [class*="hero-section"] { min-height: 280px !important; max-height: 360px !important; }
    [style*="min-height: 100vh"], [style*="min-height:100vh"] { min-height: 280px !important; }
    .hero-bg { background-size: cover !important; background-position: center !important; }
    .hero-overlay { opacity: 0.85 !important; }
    .hero-variant-split { display: block !important; max-height: 360px !important; overflow: hidden !important; }
    .hero-variant-split .hero-split { min-height: 280px !important; padding: 24px !important; gap: 16px !important; }
    .hero-variant-split .hero-split > div:last-child { height: 200px !important; }
    .hero-variant-accent { display: flex !important; align-items: stretch !important; max-height: 360px !important; overflow: hidden !important; }
    .site-theme-business .hero-section { min-height: 280px !important; }
  ` : `
    .hero-section, .hero, [class*="hero-section"] { min-height: min(100vh, 600px) !important; }
    [style*="min-height: 100vh"], [style*="min-height:100vh"] { min-height: 600px !important; }
  `;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${template?.name || 'Template Preview'}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Poppins:wght@300;400;500;600;700;800&family=Montserrat:wght@400;500;600;700;800;900&family=Raleway:wght@400;500;600;700;800&family=Lato:wght@300;400;700;900&family=Open+Sans:wght@300;400;600;700;800&family=Nunito:wght@300;400;600;700;800;900&display=swap" rel="stylesheet">
  ${extractedLinks}
  <style>
    :root {
      --primary-color: ${primaryColor};
      --secondary-color: ${secondaryColor};
      --font-family: '${fontFamily}', -apple-system, sans-serif;
    }
    *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
    html { scroll-behavior: smooth; }
    body {
      font-family: var(--font-family);
      line-height: 1.6;
      color: #1a1a1a;
      background: #fff;
      font-size: 16px;
    }
    img { max-width: 100%; height: auto; }
    a { text-decoration: none; color: inherit; }
    ul, ol { list-style: none; }
    .widget-section { position: relative; }
    .hero { position: relative; padding: 120px 20px; text-align: center; background-size: cover; background-position: center; color: #ffffff; }
    .hero-overlay { position: absolute; inset: 0; background: rgba(0,0,0,0.4); }
    .hero-content { position: relative; z-index: 1; max-width: 800px; margin: 0 auto; }
    ${thumbnailCss}
    @keyframes gradientShift { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes slideIn { from { transform: translateX(-20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
    @media (max-width: 768px) {
      [style*="grid-template-columns: repeat(3"] { grid-template-columns: 1fr !important; }
      [style*="grid-template-columns: repeat(4"] { grid-template-columns: repeat(2, 1fr) !important; }
    }
    ${css}
  </style>
</head>
<body class="${thumbnail ? 'template-thumbnail-preview' : ''}">
${html}
<script>
(function() {
  function init() {
    try { ${js} } catch(e) { console.warn('Template JS error:', e); }
    document.querySelectorAll('a[href^="#"]').forEach(function(a) {
      a.addEventListener('click', function(e) {
        var href = this.getAttribute('href');
        if (href === '#') return;
        var target = document.querySelector(href);
        if (target) { e.preventDefault(); target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
      });
    });
    document.querySelectorAll('a[href]').forEach(function(a) {
      var href = a.getAttribute('href') || '';
      if (!href.startsWith('#') && href !== '' && !href.startsWith('javascript')) {
        a.addEventListener('click', function(e) { e.preventDefault(); });
      }
    });
    document.querySelectorAll('form').forEach(function(form) {
      form.addEventListener('submit', function(e) { e.preventDefault(); });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
</script>
</body>
</html>`;
}
